import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { usePlatformAuth } from "context/PlatformAuthContext";
import { platformApiService } from "services/platformApi";
import { FaArrowLeft, FaEnvelope, FaFileAlt, FaExclamationTriangle, FaClock, FaUsers } from "react-icons/fa";
import { Modal } from "components/ui/Modal";

interface BreachReport {
  breachId: string;
  detectedAt: string;
  indicators: {
    bulkDataExport?: boolean;
    rapidSequentialExports?: boolean;
    unauthorizedAccessAttempts?: number;
    unusualAccessPatterns?: boolean;
    suspiciousActivity?: boolean;
  };
  affectedUsers: Array<{
    userId?: string;
    email: string;
    storeId?: string;
  }>;
  severity: "low" | "medium" | "high" | "critical";
  status: "detected" | "investigating" | "contained" | "resolved";
  description: string;
}

const PlatformBreachDetail: React.FC = () => {
  const { breachId } = useParams<{ breachId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = usePlatformAuth();
  const [breach, setBreach] = useState<BreachReport | null>(null);
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notifying, setNotifying] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [showAffectedUsers, setShowAffectedUsers] = useState(false);

  const fetchBreach = useCallback(async () => {
    if (!breachId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await platformApiService.getBreachById(breachId);
      setBreach(response.data);
    } catch (err: any) {
      console.error("Error fetching breach:", err);
      setError(err.response?.data?.error || "Failed to load breach details");
    } finally {
      setLoading(false);
    }
  }, [breachId]);

  const fetchLogs = useCallback(async () => {
    if (!breachId) return;

    try {
      const response = await platformApiService.getBreachLogs(breachId, { limit: 100 });
      setLogs(response.data.logs || []);
    } catch (err: any) {
      console.error("Error fetching breach logs:", err);
    }
  }, [breachId]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/platform/login", { replace: true });
      return;
    }

    fetchBreach();
    fetchLogs();
  }, [isAuthenticated, navigate, fetchBreach, fetchLogs]);

  const handleNotifyUsers = async () => {
    if (!breachId) return;

    if (!window.confirm("Are you sure you want to notify all affected users about this breach? This action cannot be undone.")) {
      return;
    }

    try {
      setNotifying(true);
      await platformApiService.notifyAffectedUsers(breachId);
      alert("Affected users have been notified successfully");
      fetchBreach(); // Refresh breach data
      fetchLogs(); // Refresh logs
    } catch (err: any) {
      console.error("Error notifying users:", err);
      alert(err.response?.data?.error || "Failed to notify users");
    } finally {
      setNotifying(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!breachId) return;

    try {
      setGeneratingReport(true);
      const response = await platformApiService.generateBreachReport(breachId);
      setBreach(response.data.report);
      alert("Breach report generated successfully");
    } catch (err: any) {
      console.error("Error generating report:", err);
      alert(err.response?.data?.error || "Failed to generate report");
    } finally {
      setGeneratingReport(false);
    }
  };

  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case "critical":
        return "bg-red-100 text-red-800 border-red-300";
      case "high":
        return "bg-orange-100 text-orange-800 border-orange-300";
      case "medium":
        return "bg-yellow-100 text-yellow-800 border-yellow-300";
      case "low":
        return "bg-blue-100 text-blue-800 border-blue-300";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300";
    }
  };

  const getHoursSinceDetection = (detectedAt: string): number => {
    const detected = new Date(detectedAt);
    const now = new Date();
    return Math.floor((now.getTime() - detected.getTime()) / (1000 * 60 * 60));
  };

  if (loading && !breach) {
    return <div className="p-8">Loading breach details...</div>;
  }

  if (error || !breach) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error || "Breach not found"}
        </div>
        <button
          onClick={() => navigate("/platform/breaches")}
          className="mt-4 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded"
        >
          Back to Breaches
        </button>
      </div>
    );
  }

  const hoursSinceDetection = getHoursSinceDetection(breach.detectedAt);
  const hoursRemaining = Math.max(0, 72 - hoursSinceDetection);

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/platform/breaches")}
          className="mb-4 flex items-center text-gray-600 hover:text-gray-900"
        >
          <FaArrowLeft className="mr-2" />
          Back to Breaches
        </button>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold">Breach Details</h1>
            <p className="text-sm text-gray-500 mt-1">Breach ID: {breach.breachId}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleGenerateReport}
              disabled={generatingReport}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <FaFileAlt />
              {generatingReport ? "Generating..." : "Generate Report"}
            </button>
            <button
              onClick={handleNotifyUsers}
              disabled={notifying || breach.affectedUsers.length === 0}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <FaEnvelope />
              {notifying ? "Notifying..." : "Notify Users"}
            </button>
          </div>
        </div>
      </div>

      {/* 72-hour Warning */}
      {hoursRemaining > 0 && hoursRemaining <= 72 && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex">
            <FaClock className="h-5 w-5 text-yellow-400 mr-3" />
            <div>
              <p className="text-sm font-medium text-yellow-800">
                ⚠️ GDPR Notification Deadline
              </p>
              <p className="text-sm text-yellow-700 mt-1">
                You have <strong>{hoursRemaining} hours</strong> remaining to notify affected users (72-hour requirement).
              </p>
            </div>
          </div>
        </div>
      )}

      {hoursRemaining <= 0 && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <div className="flex">
            <FaExclamationTriangle className="h-5 w-5 text-red-400 mr-3" />
            <div>
              <p className="text-sm font-medium text-red-800">
                ⚠️ GDPR Notification Deadline Passed
              </p>
              <p className="text-sm text-red-700 mt-1">
                The 72-hour notification window has passed. Notify affected users immediately to maintain compliance.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Breach Information */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-bold mb-4">Breach Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Severity</label>
                <div className="mt-1">
                  <span className={`px-3 py-1 text-sm font-semibold rounded-full border ${getSeverityColor(breach.severity)}`}>
                    {breach.severity.toUpperCase()}
                  </span>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <p className="mt-1 text-sm text-gray-900">{breach.status}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Detected At</label>
                <p className="mt-1 text-sm text-gray-900">{new Date(breach.detectedAt).toLocaleString()}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Hours Since Detection</label>
                <p className="mt-1 text-sm text-gray-900">{hoursSinceDetection} hours</p>
              </div>
            </div>

            <div className="mt-4">
              <label className="text-sm font-medium text-gray-500">Description</label>
              <p className="mt-1 text-sm text-gray-900">{breach.description}</p>
            </div>
          </div>

          {/* Detection Indicators */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-bold mb-4">Detection Indicators</h2>
            <div className="space-y-2">
              {breach.indicators.bulkDataExport && (
                <div className="flex items-center gap-2 text-sm">
                  <FaExclamationTriangle className="text-orange-500" />
                  <span>Bulk data export detected</span>
                </div>
              )}
              {breach.indicators.rapidSequentialExports && (
                <div className="flex items-center gap-2 text-sm">
                  <FaExclamationTriangle className="text-orange-500" />
                  <span>Rapid sequential exports detected</span>
                </div>
              )}
              {breach.indicators.unauthorizedAccessAttempts && (
                <div className="flex items-center gap-2 text-sm">
                  <FaExclamationTriangle className="text-red-500" />
                  <span>{breach.indicators.unauthorizedAccessAttempts} unauthorized access attempts</span>
                </div>
              )}
              {breach.indicators.unusualAccessPatterns && (
                <div className="flex items-center gap-2 text-sm">
                  <FaExclamationTriangle className="text-yellow-500" />
                  <span>Unusual access patterns detected</span>
                </div>
              )}
              {breach.indicators.suspiciousActivity && (
                <div className="flex items-center gap-2 text-sm">
                  <FaExclamationTriangle className="text-red-500" />
                  <span>Suspicious activity detected</span>
                </div>
              )}
            </div>
          </div>

          {/* Affected Users */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Affected Users</h2>
              <button
                onClick={() => setShowAffectedUsers(true)}
                className="text-sm text-indigo-600 hover:text-indigo-800"
              >
                View All ({breach.affectedUsers.length})
              </button>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <FaUsers />
              <span>{breach.affectedUsers.length} user(s) affected</span>
            </div>
            {breach.affectedUsers.length === 0 && (
              <p className="text-sm text-gray-500 mt-2">No affected users identified yet.</p>
            )}
          </div>

          {/* Audit Logs */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-bold mb-4">Related Logs</h2>
            {logs.length === 0 ? (
              <p className="text-sm text-gray-500">No logs found for this breach.</p>
            ) : (
              <div className="space-y-2">
                {logs.slice(0, 10).map((log, index) => (
                  <div key={index} className="border-b border-gray-200 pb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-medium">{log.action}</p>
                        <p className="text-xs text-gray-500">{new Date(log.timestamp).toLocaleString()}</p>
                      </div>
                      {log.metadata?.email && (
                        <span className="text-xs text-gray-500">{log.metadata.email}</span>
                      )}
                    </div>
                  </div>
                ))}
                {logs.length > 10 && (
                  <p className="text-sm text-gray-500 mt-2">... and {logs.length - 10} more logs</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Actions & Info */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-bold mb-4">Quick Actions</h2>
            <div className="space-y-2">
              <button
                onClick={handleNotifyUsers}
                disabled={notifying || breach.affectedUsers.length === 0}
                className="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <FaEnvelope />
                {notifying ? "Notifying..." : "Notify Affected Users"}
              </button>
              <button
                onClick={handleGenerateReport}
                disabled={generatingReport}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <FaFileAlt />
                {generatingReport ? "Generating..." : "Generate Full Report"}
              </button>
            </div>
          </div>

          {/* GDPR Compliance Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-bold text-blue-900 mb-2">GDPR Requirements</h3>
            <ul className="text-sm text-blue-800 space-y-2">
              <li>• Notify affected users within 72 hours</li>
              <li>• Document breach details and response</li>
              <li>• Implement remediation measures</li>
              <li>• Report to supervisory authority if required</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Affected Users Modal */}
      <Modal
        isOpen={showAffectedUsers}
        onClose={() => setShowAffectedUsers(false)}
        title="Affected Users"
        size="lg"
      >
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {breach.affectedUsers.length === 0 ? (
            <p className="text-sm text-gray-500">No affected users identified.</p>
          ) : (
            breach.affectedUsers.map((user, index) => (
              <div key={index} className="border-b border-gray-200 pb-2">
                <p className="text-sm font-medium">{user.email}</p>
                {user.userId && (
                  <p className="text-xs text-gray-500">User ID: {user.userId}</p>
                )}
                {user.storeId && (
                  <p className="text-xs text-gray-500">Store ID: {user.storeId}</p>
                )}
              </div>
            ))
          )}
        </div>
      </Modal>
    </div>
  );
};

export default PlatformBreachDetail;

